/**
 * Block52 Business Development Brand Configuration
 *
 * This configuration defines all branding, styling, and business-specific
 * settings for the Block52 Business Development platform.
 */

export interface BrandConfig {
  // Core Brand Identity
  siteName: string;                // Main site/brand name displayed in headers and titles
  tagline: string;                 // Short brand slogan or value proposition
  description: string;             // Longer brand description for about sections and meta tags

  // Technical Configuration
  tenantId: string;                // Unique MongoDB tenant ID for data isolation
  apiUrl?: string;                 // Optional override for GraphQL API endpoint
  brandIdentifier: string;         // Backend brand identifier for email templates and notifications

  // Logo and Images
  logo: {
    src: string;                  // Logo image URL or emoji/icon
    alt: string;                  // Alt text for accessibility
    favicon: string;              // Browser favicon path
  };

  // Contact Information
  contact: {
    businessType: string;         // Type of business
    industry: string;             // Industry category
    email?: string;               // Primary contact email
    phone?: string;               // Primary contact phone
    address?: string;             // Business physical address
    contactName?: string;         // Primary contact person name
    businessName?: string;        // Legal business name
  };

  // Color Palette
  colors: {
    // Additional accent colors
    accentBlue?: string;
    primaryBlue?: string;
    primaryBlueHover?: string;
    starYellow?: string;
    starYellowHover?: string;
    successGreen?: string;
    purpleAccent?: string;

    // Email module specific colors
    taskCardBg?: string;
    emailPreviewBg?: string;
    darkCardBg?: string;
    darkBorderSubtle?: string;

    // Highlight colors
    highlightYellow?: string;
    highlightGreen?: string;
    highlightBlue?: string;
    highlightPurple?: string;
    highlightRed?: string;

    badges?: any;

    // Primary brand colors
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryHover: string;

    // Accent colors
    accent: string;
    accentLight: string;

    // Background colors
    background: {
      main: string;
      light: string;
      card: string;
      overlay: string;
      surface: string;
      darkSurface?: string;
      cardGradient?: string;
      darkOverlay?: string;
      taskCard?: string;
      previewBg?: string;
    };

    // Status colors
    status: {
      success: string;
      warning: string;
      error: string;
      info: string;
      draft?: string;
      sent?: string;
      failed?: string;
      scheduled?: string;
      inbound?: string;
    };

    // Text colors
    text: {
      primary: string;
      secondary: string;
      muted: string;
      inverse: string;
      primaryDark?: string;
      secondaryDark?: string;
      mutedDark?: string;
    };

    // Border colors
    border: {
      light: string;
      medium: string;
      dark: string;
      darkCard?: string;
    };
  };

  // Typography
  fonts: {
    heading: string;
    body: string;
  };

  // Component styling
  components: {
    button: {
      primaryBg: string;
      primaryHover: string;
      secondaryBg: string;
      secondaryHover: string;
      whiteBg?: string;
      whiteText?: string;
      whiteHover?: string;
      blueBg?: string;
      blueHover?: string;
    };
    tooltip?: {
      bg: string;
      color: string;
      errorBg?: string;
      darkBg?: string;
    };
    menu?: {
      bg: string;
      itemBg: string;
      itemColor: string;
      itemHoverBg: string;
      itemHoverColor: string;
      borderColor: string;
    };
    card: {
      bg: string;
      border: string;
      shadow: string;
      darkShadow?: string;
      backdropFilter?: string;
      hoverShadow?: string;
    };
    navbar: {
      bg: string;
      bgSolid?: string;
      text: string;
      border?: string;
      shadow: string;
    };
    footer: {
      bg: string;
      text: string;
      linkColor: string;
      linkHover: string;
    };
    form: {
      bg: string;
      fieldBg: string;
      fieldBorder: string;
      fieldBorderFocus: string;
      fieldShadow: string;
      fieldShadowFocus: string;
      labelColor: string;
      placeholderColor: string;
    };
  };

  // Business specific
  membership: {
    totalLegacyMemberships: number;
    memberBenefits: {
      discount: string;
      features: string[];
    };
  };

}

// Block52 Business Development Configuration
export const brandConfig: BrandConfig = {
  // Core Brand Identity
  siteName: "Block52",
  tagline: "The Layer 1 blockchain for decentralized card games - secure, fair, and scalable",
  description: "Block52 is a Layer 1 blockchain built for decentralized card games. Provably fair, instant payouts, and complete transparency for any card game.",

  // Technical Configuration
  tenantId: "691d16f575ae51bfe1adf3e8",
  brandIdentifier: "Block52",

  // Logo and Images
  logo: {
    src: "/block52-logo-white.png",
    alt: "Block52 Logo",
    favicon: "/favicon.ico"
  },

  // Contact Information
  contact: {
    businessType: "Blockchain Gaming Infrastructure",
    industry: "Web3 & Blockchain",
    address: "Global - Decentralized",
    contactName: "Block52 Team",
    businessName: "Block52"
  },

  // Color Palette - Block52 Blue Theme (matching b52-iou)
  colors: {
    // Primary brand colors
    primary: "#3b82f6",           // Block52 primary blue
    primaryHover: "#2563eb",      // Darker blue on hover
    secondary: "#1a2639",         // Dark blue/navy
    secondaryHover: "#2a3f5f",    // Lighter on hover

    // Accent colors
    accent: "#64ffda",            // Teal glow accent
    accentLight: "#a7f3d0",       // Light teal

    // Background colors (Dark theme)
    background: {
      main: "#111827",            // Very dark background
      light: "#1f2937",           // Slightly lighter dark
      card: "#1f2937",            // Card background
      overlay: "rgba(59, 130, 246, 0.1)", // Blue overlay
      surface: "#374151",         // Surface color
      darkSurface: "#111827",
      cardGradient: "linear-gradient(135deg, #1a2639 0%, #2a3f5f 50%, #1a2639 100%)",
      darkOverlay: "rgba(0, 0, 0, 0.4)",
      taskCard: "rgba(31, 41, 55, 0.5)",
      previewBg: "rgba(31, 41, 55, 0.5)"
    },

    // Status colors
    status: {
      success: "#10b981",         // Green
      warning: "#f59e0b",         // Orange
      error: "#ef4444",           // Red
      info: "#3b82f6",            // Blue
      draft: "#6b7280",           // Gray
      sent: "#10b981",            // Green
      failed: "#ef4444",          // Red
      scheduled: "#3b82f6",       // Blue
      inbound: "#8b5cf6"          // Purple
    },

    // Text colors (Dark theme)
    text: {
      primary: "#ffffff",
      secondary: "rgba(255, 255, 255, 0.7)",
      muted: "rgba(255, 255, 255, 0.5)",
      inverse: "#111827",
      primaryDark: "#ffffff",
      secondaryDark: "rgba(255, 255, 255, 0.7)",
      mutedDark: "rgba(255, 255, 255, 0.5)"
    },

    // Border colors
    border: {
      light: "rgba(59, 130, 246, 0.1)",
      medium: "rgba(59, 130, 246, 0.2)",
      dark: "rgba(59, 130, 246, 0.3)",
      darkCard: "rgba(255, 255, 255, 0.1)"
    },

    // Additional colors
    accentBlue: "#3b82f6",
    primaryBlue: "#3b82f6",
    primaryBlueHover: "#2563eb",
    starYellow: "#fbbf24",
    starYellowHover: "#f59e0b",
    successGreen: "#10b981",
    purpleAccent: "#8b5cf6",

    // Email module specific colors
    taskCardBg: "rgba(31, 41, 55, 0.5)",
    emailPreviewBg: "rgba(31, 41, 55, 0.5)",
    darkCardBg: "rgba(31, 41, 55, 0.9)",
    darkBorderSubtle: "rgba(255, 255, 255, 0.05)",
    highlightYellow: "#fef3c7",
    highlightGreen: "#d1fae5",
    highlightBlue: "#dbeafe",
    highlightPurple: "#e9d5ff",
    highlightRed: "#fee2e2",

    // Badge colors
    badges: {
      blue: {
        bg: "rgba(59, 130, 246, 0.2)",
        color: "#3b82f6",
        border: "rgba(59, 130, 246, 0.3)"
      },
      green: {
        bg: "rgba(16, 185, 129, 0.2)",
        color: "#10b981",
        border: "rgba(16, 185, 129, 0.3)"
      },
      purple: {
        bg: "rgba(139, 92, 246, 0.2)",
        color: "#8b5cf6",
        border: "rgba(139, 92, 246, 0.3)"
      },
      yellow: {
        bg: "rgba(251, 191, 36, 0.2)",
        color: "#fbbf24",
        border: "rgba(251, 191, 36, 0.3)"
      },
      gray: {
        bg: "rgba(107, 114, 128, 0.2)",
        color: "#6b7280",
        border: "rgba(107, 114, 128, 0.3)"
      },
      orange: {
        bg: "rgba(245, 158, 11, 0.2)",
        color: "#f59e0b",
        border: "rgba(245, 158, 11, 0.3)"
      },
      teal: {
        bg: "rgba(100, 255, 218, 0.2)",
        color: "#64ffda",
        border: "rgba(100, 255, 218, 0.3)"
      },
      cyan: {
        bg: "rgba(34, 211, 238, 0.2)",
        color: "#22d3ee",
        border: "rgba(34, 211, 238, 0.3)"
      }
    }
  },

  // Typography
  fonts: {
    heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'SF Pro Display', system-ui, sans-serif",
    body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'SF Pro Text', system-ui, sans-serif"
  },

  // Component styling
  components: {
    button: {
      primaryBg: "#3b82f6",
      primaryHover: "#2563eb",
      secondaryBg: "rgba(59, 130, 246, 0.1)",
      secondaryHover: "rgba(59, 130, 246, 0.2)",
      whiteBg: "#ffffff",
      whiteText: "#111827",
      whiteHover: "#f3f4f6",
      blueBg: "#3b82f6",
      blueHover: "#2563eb"
    },
    tooltip: {
      bg: "#1f2937",
      color: "#ffffff",
      errorBg: "#ef4444",
      darkBg: "#1a2639"
    },
    menu: {
      bg: "rgba(31, 41, 55, 0.98)",
      itemBg: "transparent",
      itemColor: "#e5e5e5",
      itemHoverBg: "rgba(59, 130, 246, 0.2)",
      itemHoverColor: "#ffffff",
      borderColor: "rgba(59, 130, 246, 0.3)"
    },
    card: {
      bg: "#1f2937",
      border: "rgba(59, 130, 246, 0.2)",
      shadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      darkShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      hoverShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "rgba(26, 38, 57, 0.95)",        // Block52 dark navy with transparency
      bgSolid: "#1a2639",                  // Solid version for reference
      text: "#ffffff",
      border: "rgba(59, 130, 246, 0.2)",   // Block52 blue border
      shadow: "0 2px 10px 0 rgba(59, 130, 246, 0.3)"  // Blue glow shadow
    },
    footer: {
      bg: "#1a2639",
      text: "rgba(255, 255, 255, 0.7)",
      linkColor: "#3b82f6",
      linkHover: "#64ffda"
    },
    form: {
      bg: "rgba(31, 41, 55, 0.8)",
      fieldBg: "rgba(55, 65, 81, 0.8)",
      fieldBorder: "rgba(59, 130, 246, 0.2)",
      fieldBorderFocus: "#3b82f6",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
      fieldShadowFocus: "0 0 0 3px rgba(59, 130, 246, 0.2), 0 1px 2px rgba(0, 0, 0, 0.3)",
      labelColor: "#ffffff",
      placeholderColor: "rgba(255, 255, 255, 0.4)"
    }
  },

  // Business specific
  membership: {
    totalLegacyMemberships: 1000,
    memberBenefits: {
      discount: "Early access benefits",
      features: [
        "Decentralized Poker Platform Access",
        "Node Operator Rewards",
        "Governance Rights",
        "Early Token Access"
      ]
    }
  },

};

// Light theme — Block52 ships dark-only but modules import lightTheme; alias it.
export const lightTheme = {
  primary: "#007AFF",
  primaryHover: "#0051D0",
  secondary: "#5AC8FA",
  secondaryHover: "#32ADE6",
  accent: "#007AFF",
  accentLight: "#B3D9FF",
  background: {
    main: "#FFFFFF",
    light: "#F7FAFC",
    card: "#FFFFFF",
    overlay: "rgba(0, 0, 0, 0.05)",
    surface: "#F5F5F7",
    cardGradient: "linear-gradient(135deg, #FFFFFF 0%, #F7FAFC 100%)",
    darkOverlay: "rgba(0, 0, 0, 0.1)",
    taskCard: "#F7FAFC",
    previewBg: "#F7FAFC"
  },
  text: {
    primary: "#1A202C",
    secondary: "#4A5568",
    muted: "#718096",
    inverse: "#FFFFFF",
    primaryDark: "#1A202C",
    secondaryDark: "#4A5568",
    mutedDark: "#718096"
  },
  border: {
    light: "#E2E8F0",
    medium: "#CBD5E0",
    dark: "#A0AEC0",
    darkCard: "#E2E8F0"
  },
  components: {
    card: {
      bg: "#FFFFFF",
      border: "#E2E8F0",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
      darkShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
      hoverShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
      backdropFilter: "none"
    },
    navbar: {
      bg: "#FFFFFF",
      text: "#1A202C",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
    },
    footer: {
      bg: "#F7FAFC",
      text: "#4A5568",
      linkColor: "#007AFF",
      linkHover: "#0051D0"
    },
    button: {
      primaryBg: "#007AFF",
      primaryHover: "#0051D0",
      secondaryBg: "rgba(0, 122, 255, 0.1)",
      secondaryHover: "rgba(0, 122, 255, 0.2)",
      whiteBg: "#1A202C",
      whiteText: "#FFFFFF",
      whiteHover: "#2D3748",
      blueBg: "#3B82F6",
      blueHover: "#2563EB"
    }
  }
};

// Dark theme (default and only theme for Block52)
export const darkTheme = {
  primary: "#3b82f6",
  primaryHover: "#2563eb",
  secondary: "#1a2639",
  secondaryHover: "#2a3f5f",

  accent: "#64ffda",
  accentLight: "#a7f3d0",

  background: {
    main: "#111827",
    light: "#1f2937",
    card: "#1f2937",
    overlay: "rgba(59, 130, 246, 0.1)",
    surface: "#374151",
    darkSurface: "#111827",
    cardGradient: "linear-gradient(135deg, #1a2639 0%, #2a3f5f 50%, #1a2639 100%)",
    darkOverlay: "rgba(0, 0, 0, 0.4)",
    taskCard: "rgba(31, 41, 55, 0.5)",
    previewBg: "rgba(31, 41, 55, 0.5)"
  },

  text: {
    primary: "#ffffff",
    secondary: "rgba(255, 255, 255, 0.7)",
    muted: "rgba(255, 255, 255, 0.5)",
    inverse: "#111827",
    primaryDark: "#ffffff",
    secondaryDark: "rgba(255, 255, 255, 0.7)",
    mutedDark: "rgba(255, 255, 255, 0.5)"
  },

  border: {
    light: "rgba(59, 130, 246, 0.1)",
    medium: "rgba(59, 130, 246, 0.2)",
    dark: "rgba(59, 130, 246, 0.3)",
    darkCard: "rgba(255, 255, 255, 0.1)"
  },

  components: {
    card: {
      bg: "#1f2937",
      border: "rgba(59, 130, 246, 0.2)",
      shadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      darkShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      hoverShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "rgba(26, 38, 57, 0.95)",        // Block52 dark navy with transparency
      bgSolid: "#1a2639",                  // Solid version for reference
      text: "#ffffff",
      border: "rgba(59, 130, 246, 0.2)",   // Block52 blue border
      shadow: "0 2px 10px 0 rgba(59, 130, 246, 0.3)"  // Blue glow shadow
    },
    footer: {
      bg: "#1a2639",
      text: "rgba(255, 255, 255, 0.7)",
      linkColor: "#3b82f6",
      linkHover: "#64ffda"
    },
    button: {
      primaryBg: "#3b82f6",
      primaryHover: "#2563eb",
      secondaryBg: "rgba(59, 130, 246, 0.1)",
      secondaryHover: "rgba(59, 130, 246, 0.2)",
      whiteBg: "#ffffff",
      whiteText: "#111827",
      whiteHover: "#f3f4f6",
      blueBg: "#3b82f6",
      blueHover: "#2563eb"
    },
    form: {
      bg: "rgba(31, 41, 55, 0.8)",
      fieldBg: "rgba(55, 65, 81, 0.8)",
      fieldBorder: "rgba(59, 130, 246, 0.2)",
      fieldBorderFocus: "#3b82f6",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
      fieldShadowFocus: "0 0 0 3px rgba(59, 130, 246, 0.2), 0 1px 2px rgba(0, 0, 0, 0.3)",
      labelColor: "#ffffff"
    }
  }
};

// Helper function - always returns dark theme colors
export const getColor = (path: string, colorMode?: 'light' | 'dark'): string => {
  // Always use dark theme for Block52
  const theme = darkTheme;
  const keys = path.split(".");

  // Special handling for component paths
  if (keys[0] === "components") {
    let value: any = theme;
    for (const key of keys) {
      value = value[key];
      if (value === undefined) {
        let fallback: any = brandConfig.colors;
        const colorKeys = keys.slice(1);
        for (const key of colorKeys) {
          fallback = fallback[key];
          if (fallback === undefined) break;
        }
        if (fallback !== undefined) return fallback;
        console.warn(`Color path "${path}" not found in theme config`);
        return "#3b82f6";
      }
    }
    return value;
  }

  // Default handling for colors object
  let value: any = theme;
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      let fallback: any = brandConfig.colors;
      for (const key of keys) {
        fallback = fallback[key];
        if (fallback === undefined) break;
      }
      if (fallback !== undefined) return fallback;
      console.warn(`Color path "${path}" not found in theme config`);
      return "#3b82f6";
    }
  }

  return value;
};

export const getBrandValue = (path: string): any => {
  const keys = path.split(".");
  let value: any = brandConfig;

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Brand config path "${path}" not found`);
      return null;
    }
  }

  return value;
};

export const getComponent = (component: string, property: string, colorMode?: 'light' | 'dark'): string => {
  // Always use dark theme for Block52
  const theme = darkTheme;
  const componentConfig = (theme.components as any)?.[component];

  if (!componentConfig) {
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const value = fallbackConfig[property];
      if (value) return value;
    }
    console.warn(`Component "${component}" not found in theme config`);
    return "#3b82f6";
  }

  const value = componentConfig[property];
  if (!value) {
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const fallbackValue = fallbackConfig[property];
      if (fallbackValue) return fallbackValue;
    }
    console.warn(`Property "${property}" not found for component "${component}"`);
    return "#3b82f6";
  }

  return value;
};

// CSS Custom Properties Generator
export const generateCSSCustomProperties = (): string => {
  const { colors } = brandConfig;

  return `
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-accent: ${colors.accent};
      --brand-accent-light: ${colors.accentLight};

      --brand-bg-main: ${colors.background.main};
      --brand-bg-light: ${colors.background.light};
      --brand-bg-card: ${colors.background.card};
      --brand-bg-overlay: ${colors.background.overlay};
      --brand-bg-surface: ${colors.background.surface};

      --brand-success: ${colors.status.success};
      --brand-warning: ${colors.status.warning};
      --brand-error: ${colors.status.error};
      --brand-info: ${colors.status.info};

      --brand-text-primary: ${colors.text.primary};
      --brand-text-secondary: ${colors.text.secondary};
      --brand-text-muted: ${colors.text.muted};
      --brand-text-inverse: ${colors.text.inverse};

      --brand-border-light: ${colors.border.light};
      --brand-border-medium: ${colors.border.medium};
      --brand-border-dark: ${colors.border.dark};

      --brand-font-heading: ${brandConfig.fonts.heading};
      --brand-font-body: ${brandConfig.fonts.body};
    }
  `;
};

export default brandConfig;
